import { Divider, Stack, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { formatDependencyMapValue, useDependencyMapConfig } from './dependency-map-config';
import { DependencyNode } from './types';

// Square node marker side length.
const NODE_SIZE = 8;

// Outer ring on aggregate nodes to signal "group of services".
const AGGREGATE_RING_SIZE = 12;

export const LABEL_OFFSET_X = 10;
export const LABEL_OFFSET_Y = 1;
export const LABEL_FONT_SIZE = 12;
const FADED_OPACITY = 0.05;

interface DependencyMapNodeProps {
  node: DependencyNode;
  /** Pre-resolved fill (highest-priority threshold step among connected links). */
  color: string;
  connected: boolean;
  canvasBg: string;
  /** Label fill (typically the theme's primary text color). */
  labelColor: string;
  /** Per-node self-traffic values (`from === to` in the source data); drives the node tooltip. */
  selfLoops: ReadonlyMap<string, number>;
  onEnter: (id: string) => void;
  onLeave: (id: string) => void;
  onClick: (id: string) => void;
}

export const DependencyMapNode = ({
  node,
  color,
  connected,
  canvasBg,
  labelColor,
  selfLoops,
  onEnter,
  onLeave,
  onClick,
}: DependencyMapNodeProps) => {
  const { unit, metricLabel, nodeType } = useDependencyMapConfig();
  // Aggregates have no single meaningful self-traffic value.
  const selfLoopValue = node.isAggregate ? undefined : selfLoops.get(node.id);

  const group = (
    <g
      onMouseEnter={() => onEnter(node.id)}
      onMouseLeave={() => onLeave(node.id)}
      onClick={() => onClick(node.id)}
      style={{
        // Aggregates are inert; the cluster's click handler ignores them.
        cursor: node.isAggregate ? 'default' : 'pointer',
        opacity: connected ? 1 : FADED_OPACITY,
        transition: 'opacity 200ms ease',
      }}
    >
      {node.isAggregate && (
        <rect
          x={node.x - AGGREGATE_RING_SIZE / 2}
          y={node.y - AGGREGATE_RING_SIZE / 2}
          width={AGGREGATE_RING_SIZE}
          height={AGGREGATE_RING_SIZE}
          fill="none"
          stroke={color}
          strokeWidth={1}
        />
      )}
      <rect x={node.x - NODE_SIZE / 2} y={node.y - NODE_SIZE / 2} width={NODE_SIZE} height={NODE_SIZE} fill={color} />
      <text
        x={node.x + LABEL_OFFSET_X}
        y={node.y + LABEL_OFFSET_Y}
        fill={labelColor}
        stroke={canvasBg}
        strokeWidth={2}
        paintOrder="stroke"
        strokeLinejoin="round"
        fontSize={LABEL_FONT_SIZE}
        dominantBaseline="middle"
      >
        {node.label ?? node.id}
      </text>
    </g>
  );

  // Mirrors `DependencyMapLink`'s `from → to + value` layout; the `id → id`
  // line also doubles as the untruncated-name tooltip.
  if (selfLoopValue !== undefined) {
    const valueLabel = `${formatDependencyMapValue(selfLoopValue, unit)}${metricLabel ? ` ${metricLabel}` : ''}`;
    return (
      <Tooltip
        Content={
          <Stack>
            <Typography variant="body2">
              {node.id} → {node.id}
            </Typography>
            <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
              {valueLabel}
            </Typography>
          </Stack>
        }
      >
        {group}
      </Tooltip>
    );
  }

  // Aggregate tooltip: member list + guidance steering the user toward
  // filter/resize/focus rather than expanding the bucket (drilling in is
  // unbounded — large fan-outs would crash the tab).
  if (node.isAggregate) {
    return (
      <Tooltip
        Content={
          <Stack spacing={1}>
            {node.title !== undefined && node.title !== '' && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {node.title}
              </Typography>
            )}
            <Divider />
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary, fontWeight: 600 }}>
                To narrow this view:
              </Typography>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                Filter the query with labels.
              </Typography>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                Make the panel taller to fit more entries.
              </Typography>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                {`Use "Focus on ${nodeType.singular}" to drill down.`}
              </Typography>
            </Stack>
          </Stack>
        }
      >
        {group}
      </Tooltip>
    );
  }

  // Truncated real-service id; `pre-line` is unused but harmless on a single line.
  if (node.title === undefined || node.title === '') return group;
  return (
    <Tooltip
      Content={
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {node.title}
        </Typography>
      }
    >
      {group}
    </Tooltip>
  );
};
